/* eslint-disable unused-imports/no-unused-vars */
/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { isEqual } from 'lodash';
import moment from 'moment';
import { SessionType } from 'pages/sessions/interface';
import {
  IChatState, IConversation, ISelectedConversation, ISendFormInvitation, ISendFormToChats,
  ISendMessageRequestDto, IUpdateGoalRequestDto,
  SendFormInviteSucceededPayload,
} from './interface';

export const initialState: IChatState = {
  filters: {
    keyword: '',
    sessions: [],
    status: 'active',
    sessionType: 'all',
    chatType: 'all',
    page: 0,
  },
  loading: false,
  coversationLoading: false,
  sendMessageLoading: false,
  sendMeetingMessageLoading: false,
  uplaoding: false,
  conversations: [],
  selectedFormShareModalConversations: [],
  formShareModalConversations: [],
  formShareModalOpen: false,
  formShareByChatModalOpen: false,
  setFormShareThoughChatModalSuccess: false,
  total: 0,
  coversationId: null,
  conversation: {
    _id: '',
    title: '',
    users: [],
    experts: [],
    messages: [],
    createdAt: new Date().toString(),
    updatedAt: '',
    session: {
      _id: '',
      expertId: '',
      isActive: false,
      sessionType: SessionType.digitalSession,
      courseType: '',
      audienceType: '',
      title: '',
      description: '',
      message: '',
      language: '',
      expert: {
        id: '',
        firstName: '',
        lastName: '',
        profileImgUrl: '',
        bio: '',
      },
    },
    userSession: {
      _id: '',
      isCompleted: false,
      isCancel: false,
      startAt: moment().toISOString(),
      goals: [],
      isAllGoalsCompleted: false,
    },
  },
};

export const chatSlice = createSlice({
  name: 'feature/session/chat',
  initialState,
  reducers: {
    getConversations: (state: IChatState) => {
      state.loading = true;
    },
    getConversationsSucceeded: (
      state: IChatState,
      action: PayloadAction<IChatState['conversations']>,
    ) => {
      state.loading = false;
      const existingConversationsIds = state.conversations.map((message) => message._id);
      const newConversationsIds = action.payload.map((conversation) => conversation._id);
      if (!isEqual(existingConversationsIds, newConversationsIds.reverse())) {
        state.conversations = action.payload;
      }
    },
    getFormShareModalConversationsSucceeded: (
      state: IChatState,
      action: PayloadAction<{ result: IConversation[]; total: number; }>,
    ) => {
      state.loading = false;
      state.formShareModalConversations = action.payload.result;
      state.total = action.payload.total;
    },
    getConversationsFailed: (state: IChatState, action: PayloadAction<string>) => {
      state.loading = false;
    },
    setConversationId: (state: IChatState, action: PayloadAction<string | null>) => {
      state.coversationId = action.payload;
    },
    getSingleConversation: (state: IChatState) => {
      state.coversationLoading = true;
    },
    getSingleConversationSucceeded: (
      state: IChatState,
      action: PayloadAction<IChatState['conversation']>,
    ) => {
      state.coversationLoading = false;
      if (state.conversation._id === '') {
        state.conversation = action.payload;
        state.conversation.messages = action.payload.messages;
      } else {
        const existingMessageIds = state.conversation.messages.map((message) => message._id);
        const newMessageIds = action.payload.messages.map((message) => message._id);
        if (
          existingMessageIds.length === 0
          || newMessageIds.length === 0
          || !isEqual(existingMessageIds, newMessageIds)
        ) {
          state.conversation = action.payload;
          state.conversation.messages = action.payload.messages;
        }
      }

      if (action.payload.userSession.goals && action.payload.userSession.goals.length > 0) {
        const allResponseGoals = action.payload.userSession.goals;
        state.conversation.userSession.isAllGoalsCompleted = allResponseGoals.every(
          (goal) => Boolean(goal.isCompleted),
        );
        state.conversation.userSession.goals = allResponseGoals;
      }
    },
    getSingleConversationFailed: (state: IChatState, action: PayloadAction<string>) => {
      state.coversationLoading = false;
    },
    setStartUploading: (state: IChatState) => {
      state.uplaoding = true;
    },
    setEndUploading: (state: IChatState) => {
      state.uplaoding = false;
    },
    sendMessage: (state: IChatState, action: PayloadAction<ISendMessageRequestDto>) => {
      state.sendMessageLoading = true;
    },
    sendMessageSucceeded: (state: IChatState) => {
      state.sendMessageLoading = false;
    },
    sendMessageFailed: (state: IChatState, action: PayloadAction<string>) => {
      state.sendMessageLoading = false;
    },
    sendMeetingMessage: (
      state: IChatState,
      action: PayloadAction<{ resolve: any; reject: any; }>,
    ) => {
      state.sendMeetingMessageLoading = true;
    },
    sendMeetingMessageSucceeded: (state: IChatState) => {
      state.sendMeetingMessageLoading = false;
    },
    sendMeetingMessageFailed: (state: IChatState, action: PayloadAction<string>) => {
      state.sendMeetingMessageLoading = false;
    },
    completeSession: (state: IChatState, action: PayloadAction<string>) => {
      state.coversationLoading = true;
    },
    completeSessionSucceeded: (state: IChatState) => {
      state.coversationLoading = false;
      state.conversation.userSession.isCompleted = true;
    },
    completeSessionFailed: (state: IChatState, action: PayloadAction<string>) => {
      state.coversationLoading = false;
    },
    setKeyword: (state: IChatState, action: PayloadAction<string>) => {
      state.coversationId = null;
      state.filters.keyword = action.payload;
    },
    setStatus: (state: IChatState, action: PayloadAction<IChatState['filters']['status']>) => {
      state.coversationId = null;
      state.filters.status = action.payload;
    },
    setSessions: (state: IChatState, action: PayloadAction<string>) => {
      state.coversationId = null;

      if (action.payload === 'all') {
        state.filters.sessions = [];
      } else {
        state.filters.sessions = [action.payload];
      }
    },
    setSessionType: (state: IChatState, action: PayloadAction<string>) => {
      state.filters.sessionType = action.payload;
    },
    setChatType: (state: IChatState, action: PayloadAction<string>) => {
      state.filters.chatType = action.payload;
    },
    setSelectedConversations: (state: IChatState, action: PayloadAction<ISelectedConversation>) => {
      const { payload } = action;
      if (Array.isArray(payload) && payload.length === 0) {
        state.selectedFormShareModalConversations = [];
        return;
      }
      const conversation = payload;
      const index = state.selectedFormShareModalConversations.findIndex(
        (c) => c.id === conversation.id,
      );
      if (index === -1) {
        state.selectedFormShareModalConversations.push(conversation);
      } else {
        state.selectedFormShareModalConversations.splice(index, 1);
      }
    },
    clearSelectedConversations: (state: IChatState) => {
      state.selectedFormShareModalConversations = [];
    },
    clearConversations: (state: IChatState) => {
      state.formShareModalConversations = [];
    },
    sendFormToChats: (state: IChatState, action: PayloadAction<ISendFormToChats>) => {
      state.loading = true;
    },
    sendFormToChatsSucceeded: (state: IChatState) => {
      state.loading = false;
      state.selectedFormShareModalConversations = [];
      state.filters.keyword = '';
      state.filters.sessions = [];
      state.formShareModalOpen = false;
      state.filters.page = 0;
    },
    sendFormToChatsFailed: (state: IChatState) => {
      state.loading = false;
    },
    sendFormInvite(state: IChatState, action: PayloadAction<ISendFormInvitation>) {
      state.sendMessageLoading = true;
    },
    sendFormInviteSucceeded(
      state: IChatState,
      action: PayloadAction<SendFormInviteSucceededPayload | undefined>,
    ) {
      state.sendMessageLoading = false;

      if (action.payload?.isSendByTheFormListPage) {
        state.selectedFormShareModalConversations = [];
        state.filters.keyword = '';
        state.filters.sessions = [];
        state.formShareModalOpen = false;
        state.filters.page = 0;
      }
    },
    sendFormInviteFailed(state: IChatState) {
      state.sendMessageLoading = false;
    },
    setFormShareModalByChatOpen: (state: IChatState, action: PayloadAction<boolean>) => {
      state.formShareByChatModalOpen = action.payload;
    },
    setFormShareThoughChatModalSuccess: (state: IChatState, action: PayloadAction<boolean>) => {
      state.setFormShareThoughChatModalSuccess = action.payload;
    },
    setFormShareModalOpen: (state: IChatState, action: PayloadAction<boolean>) => {
      state.formShareModalOpen = action.payload;
    },
    selectAllFormShareModalConversations: (state: IChatState) => {
      const allSelected = state.formShareModalConversations.every(
        (conversation) => state.selectedFormShareModalConversations.some(
          (selected) => selected.id === conversation._id,
        ),
      );

      if (allSelected) {
        state.selectedFormShareModalConversations = state.selectedFormShareModalConversations
          .filter(
            (selectedConv) => !state.formShareModalConversations.some(
              (conv) => conv._id === selectedConv.id,
            ),
          );
      } else {
        const filteredSelected = state.selectedFormShareModalConversations.filter(
          (selectedConv) => !state.formShareModalConversations.some(
            (conv) => conv._id === selectedConv.id,
          ),
        );

        const newSelections = state.formShareModalConversations.map(
          (conversation) => ({
            id: conversation._id,
            userSessionID: conversation.userSession._id,
            title: conversation.title,
            ...(conversation.session.sessionType === SessionType.liveClass
              && { liveClassId: conversation.session._id }),
          }),
        );

        state.selectedFormShareModalConversations = [...filteredSelected, ...newSelections];
      }
    },
    setPage: (state: IChatState, action: PayloadAction<number>) => {
      state.filters.page = action.payload;
    },
    setLoading: (state: IChatState, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    clearFilters: (state: IChatState) => {
      state.filters = {
        keyword: '',
        sessions: [],
        status: 'active',
        sessionType: 'all',
        chatType: 'all',
        page: 0,
      };
    },
    updateGoals: (state: IChatState, action: PayloadAction<IUpdateGoalRequestDto>) => {
      state.coversationLoading = true;
      state.conversation.userSession.goals = state.conversation.userSession.goals.map((goal) => {
        goal.isCompleted = action.payload.ids.includes(goal._id);
        return goal;
      });
    },
    updateGoalsSucceeded: (state: IChatState) => {
      state.coversationLoading = false;
    },
    updateGoalsFailed: (state: IChatState, action: PayloadAction<string>) => {
      state.coversationLoading = false;
    },
    readAllConversationMessages: () => {},
    // ? only sets the state this doesn't trigger any saga
    setChatId: (state: IChatState, action: PayloadAction<string | null>) => {
      state.coversationId = action.payload;
    },
  },
});

export const { actions: chatActions } = chatSlice;
